import {
  Box,
  Card,
  alpha,
  Stack,
  Table,
  Tooltip,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useIntl } from 'react-intl';
import { MouseEvent, useMemo } from 'react';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import { CalendarDayEvents, CalendarEventMap, DATE_KEY_FORMAT, getMonthCells } from '../utils';

type MonthCardProps = {
  year: number;
  month: number;
  showYear?: boolean;
  eventsByDay: CalendarEventMap;
  onDaySelect: (anchorElement: HTMLElement, date: Dayjs, events: CalendarDayEvents) => void;
};

const MonthCard = ({ year, month, showYear = false, eventsByDay, onDaySelect }: MonthCardProps) => {
  const intl = useIntl();
  const monthDate = useMemo(() => dayjs().year(year).startOf('year').month(month).startOf('month'), [year, month]);
  const cells = useMemo(() => getMonthCells(year, month), [year, month]);
  const weeks = useMemo(
    () => Array.from({ length: 6 }, (_, weekIndex) => cells.slice(weekIndex * 7, weekIndex * 7 + 7)),
    [cells]
  );
  const weekdayLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) =>
        intl
          .formatDate(new Date(Date.UTC(2024, 0, 1 + index)), {
            weekday: 'short',
            timeZone: 'UTC',
          })
          .replace('.', '')
      ),
    [intl]
  );
  const monthLabel = intl.formatDate(monthDate.toDate(), { month: 'long' });
  const eventDayCount = cells.reduce((count, date) => {
    if (!date) return count;

    return eventsByDay.has(date.format(DATE_KEY_FORMAT)) ? count + 1 : count;
  }, 0);

  const getTooltipContent = (date: Dayjs, events: CalendarDayEvents) => {
    const dateLabel = intl.formatDate(date.toDate(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });

    return (
      <Stack spacing={0.5} sx={{ py: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {dateLabel}
        </Typography>

        {events.holidays.map((holiday) => (
          <Typography variant="caption" key={`holiday-${holiday.id}-${holiday.title}`}>
            {holiday.title}
            {holiday.isHalfDay ? ' · 1/2' : ''}
          </Typography>
        ))}

        {events.permissions.length > 0 && (
          <Typography variant="caption">
            {intl.formatMessage(
              {
                id: 'yearlyHoliday.peopleOnLeave',
                defaultMessage: '{count} kişi izinli',
              },
              { count: events.permissions.length }
            )}
            {`: ${events.permissions.map((permission) => permission.nameSurname).join(', ')}`}
          </Typography>
        )}

        <Typography variant="caption" sx={{ opacity: 0.72 }}>
          {intl.formatMessage({
            id: 'yearlyHoliday.openDetails',
            defaultMessage: 'Detayları görmek için seçin',
          })}
        </Typography>
      </Stack>
    );
  };

  return (
    <Card
      component="section"
      variant="outlined"
      sx={{
        p: 1.5,
        minWidth: 0,
        borderRadius: 3,
        backgroundColor: 'background.paper',
        transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
        '&:hover': {
          borderColor: (theme) => alpha(theme.palette.success.main, 0.36),
          boxShadow: (theme) => theme.shadows[1],
          transform: 'translateY(-1px)',
        },
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5, pb: 0.75 }}>
        <Stack direction="row" alignItems="baseline" spacing={0.65}>
          <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 750, textTransform: 'capitalize' }}>
            {monthLabel}
          </Typography>
          {showYear && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
              {year}
            </Typography>
          )}
        </Stack>

        {eventDayCount > 0 && (
          <Box
            sx={{
              px: 0.8,
              py: 0.2,
              borderRadius: 5,
              color: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
              backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12),
              fontSize: '0.68rem',
              fontWeight: 700,
              lineHeight: 1.5,
            }}>
            {intl.formatMessage(
              { id: 'yearlyHoliday.markedDays', defaultMessage: '{count} işaretli gün' },
              { count: eventDayCount }
            )}
          </Box>
        )}
      </Stack>

      <Table
        size="small"
        sx={{ tableLayout: 'fixed' }}
        aria-label={intl.formatMessage(
          { id: 'yearlyHoliday.monthCalendarAria', defaultMessage: '{month} {year} takvimi' },
          { month: monthLabel, year }
        )}>
        <TableHead>
          <TableRow>
            {weekdayLabels.map((weekday, index) => (
              <TableCell
                component="th"
                scope="col"
                align="center"
                key={`${weekday}-${index}`}
                sx={{
                  p: 0.25,
                  height: 24,
                  border: 0,
                  color: index > 4 ? 'text.disabled' : 'text.secondary',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}>
                {weekday}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {weeks.map((week, weekIndex) => (
            <TableRow key={`week-${weekIndex}`}>
              {week.map((date, dayIndex) => {
                if (!date) return <TableCell key={`empty-${weekIndex}-${dayIndex}`} sx={{ p: 0.25, border: 0 }} />;

                const dateKey = date.format(DATE_KEY_FORMAT);
                const events = eventsByDay.get(dateKey);
                const isWeekend = date.day() === 0 || date.day() === 6;
                const isToday = date.isSame(dayjs(), 'day');
                const hasHoliday = !!events?.holidays.length;
                const hasPermissions = !!events?.permissions.length;
                const hasEvents = hasHoliday || hasPermissions;
                const isHalfDay =
                  !!events &&
                  (events.holidays.some((holiday) => holiday.isHalfDay) ||
                    events.permissions.some((permission) => permission.isHalfDay));
                const dateLabel = intl.formatDate(date.toDate(), {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                });
                const ariaDetails = [
                  isToday ? intl.formatMessage({ id: 'yearlyHoliday.today', defaultMessage: 'Bugün' }) : null,
                  isWeekend ? intl.formatMessage({ id: 'yearlyHoliday.weekend', defaultMessage: 'Hafta sonu' }) : null,
                  ...(events?.holidays || []).map((holiday) => holiday.title),
                  hasPermissions
                    ? intl.formatMessage(
                        { id: 'yearlyHoliday.peopleOnLeave', defaultMessage: '{count} kişi izinli' },
                        { count: events?.permissions.length || 0 }
                      )
                    : null,
                  isHalfDay ? intl.formatMessage({ id: 'yearlyHoliday.halfDay', defaultMessage: 'Yarım gün' }) : null,
                ].filter(Boolean);

                const handleClick = (event: MouseEvent<HTMLElement>) => {
                  if (events && hasEvents) onDaySelect(event.currentTarget, date, events);
                };

                return (
                  <TableCell key={dateKey} align="center" sx={{ p: 0.25, border: 0 }}>
                    <Tooltip
                      title={events && hasEvents ? getTooltipContent(date, events) : ''}
                      arrow
                      disableTouchListener>
                      <span style={{ display: 'block' }}>
                        <Box
                          component={hasEvents ? 'button' : 'span'}
                          type={hasEvents ? 'button' : undefined}
                          onClick={hasEvents ? handleClick : undefined}
                          aria-label={
                            hasEvents
                              ? `${dateLabel}${ariaDetails.length ? `: ${ariaDetails.join(', ')}` : ''}`
                              : undefined
                          }
                          sx={{
                            position: 'relative',
                            display: 'block',
                            width: '100%',
                            minHeight: 35,
                            p: 0,
                            aspectRatio: '1 / 1',
                            appearance: 'none',
                            font: 'inherit',
                            borderRadius: 1.25,
                            border: '1px solid',
                            borderColor: (theme) => {
                              if (isToday) return theme.palette.primary.main;
                              if (hasEvents) return alpha(theme.palette.success.main, 0.42);
                              return 'transparent';
                            },
                            borderWidth: isToday ? 2 : 1,
                            backgroundColor: (theme) => {
                              if (hasHoliday && hasPermissions) return alpha(theme.palette.success.main, 0.26);
                              if (hasHoliday) return alpha(theme.palette.success.main, 0.21);
                              if (hasPermissions) return alpha(theme.palette.success.main, 0.14);
                              if (isWeekend) return theme.palette.action.disabledBackground;
                              return 'transparent';
                            },
                            backgroundImage: (theme) =>
                              isWeekend && hasEvents
                                ? `repeating-linear-gradient(135deg, transparent 0, transparent 5px, ${alpha(
                                    theme.palette.grey[500],
                                    theme.palette.mode === 'dark' ? 0.16 : 0.1
                                  )} 5px, ${alpha(
                                    theme.palette.grey[500],
                                    theme.palette.mode === 'dark' ? 0.16 : 0.1
                                  )} 8px)`
                                : 'none',
                            color: (theme) => {
                              if (hasEvents) {
                                return theme.palette.mode === 'dark'
                                  ? theme.palette.success.light
                                  : theme.palette.success.dark;
                              }

                              return isWeekend ? theme.palette.text.disabled : theme.palette.text.primary;
                            },
                            cursor: hasEvents ? 'pointer' : 'default',
                            transition: 'transform 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
                            '&:hover': hasEvents
                              ? {
                                  transform: 'scale(1.06)',
                                  backgroundColor: (theme) => alpha(theme.palette.success.main, 0.3),
                                  boxShadow: (theme) => `0 3px 10px ${alpha(theme.palette.success.main, 0.18)}`,
                                  zIndex: 1,
                                }
                              : undefined,
                            '&:focus-visible': {
                              outline: (theme) => `3px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                              outlineOffset: 1,
                            },
                          }}>
                          <Typography
                            component="span"
                            sx={{
                              position: 'absolute',
                              top: 3,
                              left: 5,
                              fontSize: '0.72rem',
                              fontWeight: hasEvents || isToday ? 750 : 500,
                              lineHeight: 1,
                            }}>
                            {date.date()}
                          </Typography>

                          {isHalfDay && (
                            <Typography
                              component="span"
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 3,
                                px: 0.25,
                                borderRadius: 0.5,
                                backgroundColor: (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.common.black, 0.38)
                                    : alpha(theme.palette.common.white, 0.72),
                                color: (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? theme.palette.success.light
                                    : theme.palette.success.dark,
                                fontSize: '0.5rem',
                                fontWeight: 900,
                                lineHeight: 1.35,
                              }}>
                              1/2
                            </Typography>
                          )}

                          {hasEvents && (
                            <Stack
                              component="span"
                              direction="row"
                              alignItems="center"
                              spacing={0.25}
                              sx={{ position: 'absolute', left: 4, right: 4, bottom: 3, minHeight: 10 }}>
                              {hasHoliday && <EventRoundedIcon sx={{ fontSize: 10 }} aria-hidden="true" />}

                              {hasPermissions && (
                                <Stack
                                  component="span"
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.15}
                                  sx={{ ml: 'auto !important' }}>
                                  <PeopleAltRoundedIcon sx={{ fontSize: 10 }} aria-hidden="true" />
                                  <Typography
                                    component="span"
                                    sx={{ fontSize: '0.54rem', fontWeight: 800, lineHeight: 1 }}>
                                    {events?.permissions.length}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          )}
                        </Box>
                      </span>
                    </Tooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default MonthCard;
